import { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { Helpers } from '../../../utils/helpers';
import { useToaster } from '../../../utils/hooks';
import { ToastStyle } from '../../../utils/hooks/useToaster';
import styles from './DateInput.module.css';

// Custom date input component that handles partial inputs during typing
interface DateInputProps {
  value: Date | null;
  onChange: (date: Date | null) => void;
  isInvalid?: boolean;
  required?: boolean;
  label?: string;
  size?: 'sm' | 'lg';
  disabled?: boolean;
}

// Function to create a timezone-safe date from a date string
const createLocalDate = (dateString: string): Date | null => {
  // If dateString is empty, return null
  if (!dateString) {
    return null;
  }

  // For partial dates during typing, we still want to keep the value
  // HTML date inputs will always format as YYYY-MM-DD when valid
  // We only convert to Date object once we have a valid date
  if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateString.split('-').map(Number);

    // Additional validation to ensure the date parts are valid
    if (year < 1000 || month <= 0 || month > 12 || day <= 0 || day > 31) {
      return null;
    }

    const date = new Date();
    date.setFullYear(year);
    date.setMonth(month - 1); // Month is 0-based
    date.setDate(day);
    date.setHours(12, 0, 0, 0); // Set to noon (12:00) instead of midnight
    return date;
  }

  // For partial inputs, just return the current value from state
  // This preserves the input during typing
  return null;
};

export const DateInput = ({
  value,
  onChange,
  isInvalid,
  required,
  label,
  size = 'sm',
  disabled = false
}: DateInputProps) => {
  // Keep track of the string value during editing
  const [inputValue, setInputValue] = useState<string>(() => Helpers.toDateString(value) || '');
  const [isFocused, setIsFocused] = useState(false);
  const toaster = useToaster();

  // Handle change of the input field
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);

    // Only convert to Date when it's a complete valid date
    if (newValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      onChange(createLocalDate(newValue));
    } else if (!newValue) {
      onChange(null);
    }
    // Otherwise, keep the current Date value (don't clear it during typing)
  };

  // When the input loses focus, validate the date
  const handleBlur = () => {
    setIsFocused(false);

    // If it's not empty and not a valid date format, show a toast and reset
    if (inputValue && !inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Show toast notification
      toaster.addToast(
        `Invalid date format for ${label || 'date field'}. Please use YYYY-MM-DD format.`,
        ToastStyle.WARNING,
        3000
      );

      // Reset to the previous valid date
      setInputValue(Helpers.toDateString(value) || '');
    } else if (inputValue && inputValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
      // Check if the date is valid (not just the format)
      const [year, month, day] = inputValue.split('-').map(Number);
      const date = new Date(year, month - 1, day);

      if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
        // Invalid date (e.g., February 30)
        toaster.addToast(
          `Invalid date: ${inputValue}. Please enter a valid date.`,
          ToastStyle.WARNING,
          3000
        );

        // Reset to the previous valid date
        setInputValue(Helpers.toDateString(value) || '');
      }
    }
  };

  // Update the input value if the date changes externally
  // But don't update during focus to avoid typing conflicts
  useEffect(() => {
    if (!isFocused) {
      setInputValue(Helpers.toDateString(value) || '');
    }
  }, [value, isFocused]);

  return (
    <Form.Group className={styles.compactFormGroup}>
      {label && <Form.Label className={required ? styles.required : undefined}>{label}</Form.Label>}
      <Form.Control
        size={size}
        type="date"
        disabled={disabled}
        value={inputValue}
        onChange={handleChange}
        onFocus={() => setIsFocused(true)}
        onBlur={handleBlur}
        isInvalid={isInvalid}
      />
    </Form.Group>
  );
};
